import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import {
    filterAircraftItems,
    filterBunos,
    formatDaysRemaining,
    formatDueDate,
    formatHoursRemaining,
    getCalendarBand,
    getHoursBand,
    remainingCellColor,
} from './aircraftComponentLifeUtils';

const STATUS_COLORS = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bb',
    AWP: '#335d80',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d',
};

function getStatusColor(status) {
    return STATUS_COLORS[status] || '#6c757d';
}

function buildStatusByBuno(records) {
    const map = {};
    (records || []).forEach(ac => {
        if (ac.buno) {
            map[String(ac.buno).trim()] = ac.status || '';
        }
    });
    return map;
}

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        section: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
        },
        pageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '10px',
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '16px',
        },
        card: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 8px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: '0 0 16px 0',
        },
        controls: {
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '180px',
            maxWidth: '100%',
        },
        bunoSelect: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '280px',
            maxWidth: '100%',
            flex: '1 1 280px',
        },
        searchInput: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '200px',
        },
        button: {
            padding: '8px 16px',
            backgroundColor: colors.link,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        },
        empty: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        detailTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '8px',
            fontSize: '13px',
        },
        tableWrap: {
            maxHeight: 'calc(100vh - 280px)',
            overflowY: 'auto',
            marginTop: '8px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
        },
        th: {
            textAlign: 'left',
            padding: '8px',
            borderBottom: `2px solid ${colors.border}`,
            color: colors.text,
            position: 'sticky',
            top: 0,
            backgroundColor: colors.cardBg,
        },
        td: {
            padding: '8px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
        },
        statusRow: {
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '12px',
            color: colors.text,
            fontSize: '14px',
        },
        fallbackText: colors.text,
    };
};

function AircraftComponentLifePage({ theme }) {
    const styles = getStyles(theme);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedBuno = searchParams.get('buno') || '';
    const [siteFilter, setSiteFilter] = useState(searchParams.get('site_name') || '');
    const [sites, setSites] = useState([]);
    const [bunos, setBunos] = useState([]);
    const [bunoSearch, setBunoSearch] = useState('');
    const [itemSearch, setItemSearch] = useState('');
    const [items, setItems] = useState([]);
    const [aircraftMeta, setAircraftMeta] = useState({ modex: '', site_name: '' });
    const [statusByBuno, setStatusByBuno] = useState({});
    const [loadingBunos, setLoadingBunos] = useState(true);
    const [loadingItems, setLoadingItems] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        api.get('/sites/')
            .then(res => setSites(res.data.results || res.data || []))
            .catch(err => console.error('Error fetching sites:', err));
    }, []);

    useEffect(() => {
        api.get('/aircraft-status/')
            .then(res => {
                const statuses = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setStatusByBuno(buildStatusByBuno(statuses));
            })
            .catch(err => console.error('Error fetching aircraft status:', err));
    }, []);

    const fetchBunos = useCallback(() => {
        setLoadingBunos(true);
        const params = new URLSearchParams();
        if (siteFilter) params.append('site_name', siteFilter);
        const qs = params.toString();
        api.get(`/crd-all/bunos/${qs ? `?${qs}` : ''}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setBunos(data);
            })
            .catch(err => {
                console.error('Error fetching CRD BUNOs:', err);
                setAlert({ show: true, message: 'Failed to fetch aircraft list.', type: 'error' });
                setBunos([]);
            })
            .finally(() => setLoadingBunos(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchBunos();
    }, [fetchBunos]);

    const fetchAircraft = useCallback(() => {
        if (!selectedBuno) {
            setItems([]);
            setAircraftMeta({ modex: '', site_name: '' });
            return;
        }
        setLoadingItems(true);
        const params = new URLSearchParams();
        params.append('buno', selectedBuno);
        if (siteFilter) params.append('site_name', siteFilter);
        api.get(`/crd-all/aircraft/?${params.toString()}`)
            .then(res => {
                const payload = res.data || {};
                setItems(Array.isArray(payload.items) ? payload.items : []);
                setAircraftMeta({
                    modex: payload.modex || '',
                    site_name: payload.site_name || '',
                });
            })
            .catch(err => {
                console.error('Error fetching aircraft components:', err);
                setAlert({ show: true, message: 'Failed to fetch aircraft component life.', type: 'error' });
                setItems([]);
            })
            .finally(() => setLoadingItems(false));
    }, [selectedBuno, siteFilter]);

    useEffect(() => {
        fetchAircraft();
    }, [fetchAircraft]);

    const updateParams = useCallback((nextBuno, nextSite) => {
        const params = new URLSearchParams();
        if (nextBuno) params.set('buno', nextBuno);
        if (nextSite) params.set('site_name', nextSite);
        setSearchParams(params);
    }, [setSearchParams]);

    const filteredBunos = useMemo(() => {
        const filtered = filterBunos(bunos, bunoSearch);
        if (selectedBuno && !filtered.some(row => row.buno === selectedBuno)) {
            const selected = bunos.find(row => row.buno === selectedBuno);
            if (selected) return [selected, ...filtered];
        }
        return filtered;
    }, [bunos, bunoSearch, selectedBuno]);

    const filteredItems = useMemo(
        () => filterAircraftItems(items, itemSearch),
        [items, itemSearch],
    );

    const acStatus = statusByBuno[String(selectedBuno).trim()] || '';

    return (
        <div style={styles.section}>
            <CustomAlert
                show={alert.show}
                message={alert.message}
                type={alert.type}
                theme={theme}
                onClose={() => setAlert({ show: false, message: '', type: '' })}
            />

            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme} />
                <Link to="/reports" style={styles.navLink}>&larr; Back to Reports</Link>
            </div>

            <div style={styles.card}>
                <h2 style={styles.title}>Aircraft Component Life</h2>
                <p style={styles.subtitle}>
                    Select a BUNO to see every CRD requirement on that aircraft, with hours remaining and calendar days remaining.
                </p>

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => {
                            const nextSite = e.target.value;
                            setSiteFilter(nextSite);
                            updateParams('', nextSite);
                            setBunoSearch('');
                            setItemSearch('');
                        }}
                        style={styles.select}
                        aria-label="Site filter"
                    >
                        <option value="">All Sites</option>
                        {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <input
                        type="search"
                        value={bunoSearch}
                        onChange={(e) => setBunoSearch(e.target.value)}
                        placeholder="Search BUNO or modex…"
                        style={styles.searchInput}
                        aria-label="Search aircraft"
                    />
                    <select
                        value={selectedBuno}
                        onChange={(e) => updateParams(e.target.value, siteFilter)}
                        style={styles.bunoSelect}
                        aria-label="Aircraft"
                        disabled={loadingBunos}
                    >
                        <option value="">
                            {loadingBunos ? 'Loading aircraft…' : 'Select a BUNO'}
                        </option>
                        {filteredBunos.map(row => (
                            <option key={`${row.site_name}-${row.buno}`} value={row.buno}>
                                {row.buno}
                                {row.modex ? ` — ${row.modex}` : ''}
                                {` (${row.component_count})`}
                            </option>
                        ))}
                    </select>
                    <input
                        type="search"
                        value={itemSearch}
                        onChange={(e) => setItemSearch(e.target.value)}
                        placeholder="Search nomenclature, PN, position, serial…"
                        style={styles.searchInput}
                        aria-label="Search components"
                    />
                    <button type="button" onClick={selectedBuno ? fetchAircraft : fetchBunos} style={styles.button}>
                        Refresh Data
                    </button>
                </div>

                {loadingBunos && !selectedBuno ? (
                    <div style={styles.loading}><Spinner /></div>
                ) : bunos.length === 0 ? (
                    <div style={styles.empty}>
                        No CRD All data available. Upload a CRD All (FOUO CRD) file on the Upload Data page.
                    </div>
                ) : !selectedBuno ? (
                    <div style={styles.empty}>
                        Select a BUNO to view component life. {filteredBunos.length} aircraft available
                        {bunoSearch ? ' matching the search' : ''}.
                    </div>
                ) : loadingItems ? (
                    <div style={styles.loading}><Spinner /></div>
                ) : filteredItems.length === 0 ? (
                    <div style={styles.empty}>
                        No component requirements for this aircraft
                        {itemSearch ? ' matching the search' : ''}.
                    </div>
                ) : (
                    <>
                        <div style={styles.statusRow}>
                            <span><strong>BUNO:</strong> {selectedBuno}</span>
                            <span>
                                <strong>Status:</strong>{' '}
                                <span style={{ color: acStatus ? getStatusColor(acStatus) : styles.fallbackText, fontWeight: 'bold' }}>
                                    {acStatus || '—'}
                                </span>
                            </span>
                            <span><strong>Modex:</strong> {aircraftMeta.modex || '—'}</span>
                            <span><strong>Site:</strong> {aircraftMeta.site_name || '—'}</span>
                            <span>
                                <strong>Requirements:</strong> {filteredItems.length}
                                {itemSearch && items.length !== filteredItems.length ? ` of ${items.length}` : ''}
                            </span>
                        </div>
                        <div style={styles.tableWrap}>
                            <table style={styles.detailTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Nomenclature</th>
                                        <th style={styles.th}>Part Number</th>
                                        <th style={styles.th}>Position</th>
                                        <th style={styles.th}>Serial</th>
                                        <th style={styles.th}>Type</th>
                                        <th style={styles.th}>Hours Remaining</th>
                                        <th style={styles.th}>Calendar Days Remaining</th>
                                        <th style={styles.th}>Due Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map(item => {
                                        const hoursBand = getHoursBand(item.hours_remaining);
                                        const daysBand = getCalendarBand(item.calendar_days_remaining);
                                        return (
                                            <tr key={item.id != null ? item.id : `${item.pn}-${item.pos_cd}-${item.serno}`}>
                                                <td style={styles.td}>{item.nomen || '—'}</td>
                                                <td style={styles.td}>{item.pn || '—'}</td>
                                                <td style={styles.td}>{item.pos_cd || '—'}</td>
                                                <td style={styles.td}>{item.serno || '—'}</td>
                                                <td style={styles.td}>{item.bl_no_ind_cd || '—'}</td>
                                                <td style={{
                                                    ...styles.td,
                                                    fontWeight: hoursBand && hoursBand !== 'remaining' ? 'bold' : 'normal',
                                                    color: remainingCellColor(hoursBand, styles.td.color),
                                                }}>
                                                    {formatHoursRemaining(item.hours_remaining)}
                                                </td>
                                                <td style={{
                                                    ...styles.td,
                                                    fontWeight: daysBand && daysBand !== 'remaining' ? 'bold' : 'normal',
                                                    color: remainingCellColor(daysBand, styles.td.color),
                                                }}>
                                                    {formatDaysRemaining(item.calendar_days_remaining)}
                                                </td>
                                                <td style={styles.td}>{formatDueDate(item.schd_dt)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AircraftComponentLifePage;
